import {FormControl, FormField} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';

import {useWatch, UseFormReturn} from 'react-hook-form';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleSelectionFilterFields} from '../../VehicleSelectionFilterFields/VehicleSelectionFilterFields';
import {PromoPhotosFormType} from '../types/PromoPhotosFormType';
import {VehicleSelectionMode} from '../types/VehicleSelectionMode';
import {getPromoPhotoVehicleSelectionBinding} from '../utils/getPromoPhotoVehicleSelectionBinding';

const VEHICLE_SELECTION_MODE_OPTIONS: {value: VehicleSelectionMode; label: string}[] = [
  {
    value: VehicleSelectionMode.ALL_VEHICLES,
    label: i18n.t('entity.salesCampaign.membershipType.allVehicles'),
  },
  {
    value: VehicleSelectionMode.BY_CONDITIONS,
    label: i18n.t('entity.promoPhotos.vehicleSelection.labels.byConditions'),
  },
];

interface PromoPhotoVehicleSelectionSectionProps extends RequiredTestIdProps {
  control: FormControl<PromoPhotosFormType>;
  formApi: UseFormReturn<PromoPhotosFormType>;
}

// "Apply to vehicles" of a REGULAR photo: every vehicle in sale, or only the ones matching the
// conditions. The conditions stay in the form while "all vehicles" is chosen, so switching back
// restores them; only "by conditions" sends them.
export function PromoPhotoVehicleSelectionSection(props: PromoPhotoVehicleSelectionSectionProps) {
  const [vehicleSelectionMode, filter] = useWatch({
    control: props.control,
    name: ['vehicleSelectionMode', 'filter'],
  });

  return (
    <VStack spacing={4}>
      <FormField
        control={props.control}
        type="radio"
        name="vehicleSelectionMode"
        label={i18n.t('entity.promoPhotos.vehicleSelection.labels.applyToVehicles')}
        options={VEHICLE_SELECTION_MODE_OPTIONS}
        data-testid={suffixTestId('mode', props)}
      />
      <Show when={vehicleSelectionMode === VehicleSelectionMode.BY_CONDITIONS}>
        <VStack spacing={1}>
          <Heading size={5}>{i18n.t('entity.interest.labels.conditions')}</Heading>
          <Text size="xSmall" color="tertiary">
            {i18n.t('entity.promoPhotos.vehicleSelection.labels.conditionsDescription')}
          </Text>
        </VStack>
        <VehicleSelectionFilterFields
          control={props.control}
          binding={getPromoPhotoVehicleSelectionBinding(props.formApi)}
          filter={filter}
          areExtendedOperatorsEnabled
          data-testid={suffixTestId('filter', props)}
        />
      </Show>
    </VStack>
  );
}
